'use client';

/**
 * Enhanced-UI mode — an OPT-IN visual polish layer ("v3").
 *
 * Off by default (existing users see no change). When on, `<html>` carries
 * `data-ui="enhanced"`, which globals.css uses to layer in elevation, the
 * signature conviction gauge styling, live-number flashes, freshness pulses,
 * and refined radii/focus — all reversible by flipping one attribute.
 *
 * Two consumption modes:
 *  - CSS-driven surfaces just render a stable class (e.g. `sig-elev`,
 *    `sig-freshness-live`); the styling only applies under [data-ui="enhanced"].
 *    No hook needed.
 *  - STRUCTURAL swaps (render the "Since you last looked" strip, swap the
 *    conviction gauge) use useEnhancedUi() to branch in React.
 *
 * Persistence mirrors the theme system: localStorage key 'sig-ui-enhanced',
 * set pre-paint by the UI_GUARD in app/layout.tsx so there's no flash. The
 * toggle dispatches 'sig-ui-change' so every hook instance updates live.
 */

import { useEffect, useState } from 'react';

export const UI_STORAGE_KEY = 'sig-ui-enhanced';
const UI_EVENT = 'sig-ui-change';

/** Read the current mode from the DOM attribute (source of truth post-paint). */
function readEnhanced(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-ui') === 'enhanced';
}

/** Reactive flag — false on SSR/first paint, syncs on mount + on any change. */
export function useEnhancedUi(): boolean {
  const [enhanced, setEnhanced] = useState(false);
  useEffect(() => {
    setEnhanced(readEnhanced());
    const handler = () => setEnhanced(readEnhanced());
    window.addEventListener(UI_EVENT, handler);
    return () => window.removeEventListener(UI_EVENT, handler);
  }, []);
  return enhanced;
}

/** Flip the mode: persist, set the attribute pre-render, broadcast to hooks. */
export function setEnhancedUi(on: boolean): void {
  try {
    localStorage.setItem(UI_STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* private mode — attribute still applies for the session */
  }
  if (on) document.documentElement.setAttribute('data-ui', 'enhanced');
  else document.documentElement.removeAttribute('data-ui');
  window.dispatchEvent(new Event(UI_EVENT));
}
